import { PayloadAction } from '@reduxjs/toolkit';
import {
  ListInfoModel,
  ListsAdditionalUserInfo,
  ListsUserInfo,
  UserModel,
} from '$store/models';

export interface ListsState {
  lists: ListsMap;
  custom: ListInfoModel[];
  listsDataMap: ListDataMap;
  users: ListsUserInfo[];
  isLoading: boolean;
  isListLoading: boolean;
}

interface ListsMap {
  [key: ListInfoModel['listId']]: ListInfoModel;
}

interface UsersMap {
  [key: UserModel['id']]: UserModel;
}

interface followedIds {
  [key: string]: boolean;
}

export enum DefaultListTypes {
  TIPS = 'tips',
  PAID_MESSAGES = 'paid_messages',
  FOLLOWINGS = 'followings',
  SPONSORS = 'sponsors',
  SUMMARY = 'summary',
  SUBSCRIBERS = 'subscribers',
  TELEGRAM = 'telegram',
  REFERRALS = 'referrals',
}

type ListTypes = DefaultListTypes | string;

type IncomePaymentList<
  T extends string,
  K extends string,
  V,
  U extends DefaultListTypes,
> = {
  [key in T]: Array<
    {
      [key in K]: V[];
    } & {
      total: number;
      userId: number;
    }
  >;
} & {
  total: number;
  users: UsersMap;
  listId: U;
  followed: followedIds;
};

type TipsList = IncomePaymentList<
  'senders',
  'tips',
  {
    amount: number;
    createdAt: number;
    id: number;
    message: string;
    paid: number;
    postId: number;
  },
  DefaultListTypes.TIPS
>;

type PaidMessagesList = IncomePaymentList<
  'buyers',
  'messages',
  {
    createdAt: number;
    id: number;
    price: number;
    text: string;
  },
  DefaultListTypes.PAID_MESSAGES
>;

type FollowingsList = {
  followings: UserModel[];
  totalCount: number;
  listId: DefaultListTypes.FOLLOWINGS;
};

export type SponsorsList = {
  sponsors: (ListsAdditionalUserInfo & { user: UserModel })[];
  total: number;
  listId: DefaultListTypes.SPONSORS;
  followed: followedIds;
};

export type SummaryList = {
  summary: [ListsAdditionalUserInfo & { userId: number }];
  followed: followedIds;
  users: UsersMap;
  total: number;
  listId: DefaultListTypes.SUMMARY;
};

type SubscribersList = {
  subscribers: UserModel[];
  followed: followedIds;
  totalCount: number;
  listId: DefaultListTypes.SUBSCRIBERS;
};

type TelegramList = {
  followers: {
    followed: boolean;
    modelId: number;
    stopped: number;
    userId: number;
    user: UserModel;
    summary: ListsAdditionalUserInfo & { userId: number };
  }[];
  totalCount: number;
  listId: DefaultListTypes.TELEGRAM;
};

type ReferralsList = {
  referrals: UserModel[];
  totalCount: number;
  listId: DefaultListTypes.REFERRALS;
};

export type CustomList = {
  users: UserModel[];
  totalCount: number;
  listId: string;
  list: ListInfoModel;
};

export type ListDataTypes =
  | TipsList
  | PaidMessagesList
  | FollowingsList
  | SponsorsList
  | SubscribersList
  | TelegramList
  | ReferralsList;
//| CustomList;

export type ListDataMap = {
  [key in ListDataTypes['listId']]?: ListDataTypes;
};

export type SetListsAction = PayloadAction<
  { users: ListsUserInfo[]; custom: ListInfoModel[] } & ListsMap
>;
export type LoadListDataAction = PayloadAction<ListTypes>;
export type SetListDataAction = PayloadAction<{
  listData: ListDataTypes;
  listId: ListTypes;
}>;
